// Adapted from: https://dev.to/finiam/predictable-react-authentication-with-the-context-api-g10
import Bugsnag from '@bugsnag/js'

import queryString from 'query-string'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import * as sessionsApi from 'api/sessions'
import { apiClient } from 'api/client'
import * as usersApi from 'api/users'
import {
  confirmEmailPath,
  dashboardPath,
  loginPath,
  resetPasswordPath,
  verifyOtpPath
} from 'components/Routes'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Profile = Record<string, any>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SecuritiesOwned = Record<string, any>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type User = Record<string, any>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Wallet = Record<string, any>
import { checkTypeOfValue, sentenceCase } from 'utils/util'
import {
  clearTokens,
  decodeAccessToken,
  getTokens,
  isAccessTokenExpired,
  setTokens
} from 'utils/jwtStorage'
import {
  cancelRefresh,
  refreshAccessToken,
  scheduleRefresh,
  startMultiTabSync
} from 'utils/jwtRefresh'

import { Alert, AlertColor, Snackbar } from '@mui/material'
import { AuthorizationStatus, VerifyOtpParams } from 'api/sessions/sessions'
import LoadingWrapper from 'components/layout/LoadingWrapper'
import PopUp from 'components/PopUp'
import {
  getUserTheme,
  saveTheme
} from 'components/pages/DeviceSettings/DeviceSettings'
import { fbEvent, gaEvent } from 'utils/ga'
import { clearAttribution, signupAttribution } from 'utils/attribution'
import AuthModal from './AuthModal'

export interface UserType
  extends Omit<User, 'profile' | 'securitiesOwned' | 'wallet'> {
  profile?: Partial<Profile> | null
  securitiesOwned?: [Partial<SecuritiesOwned>] | null
  wallet?: Partial<Wallet> | null
}

export interface IToast {
  open: boolean
  message: string
  type?: AlertColor
  duration?: number
}

export interface SignUpPayload {
  email: string
  password: string
  signUpToken: string
  recaptchaToken?: string
  referrerCode?: string
  marketingOptedInAt: boolean
}

export interface LogInPayload {
  email: string
  password: string
  keepMeSignedIn: string
  recaptchaToken?: string
}

// MFA challenge state set during login when Cognito returns a challenge
interface MfaChallengeState {
  email: string
  session: string
  challengeName: string
}

interface AuthContextType {
  user?: UserType
  loading: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any
  login: ({
    email,
    password,
    keepMeSignedIn,
    recaptchaToken
  }: LogInPayload) => void
  resend: (email: string, recaptchaToken?: string) => void
  requestReset: (email: string, recaptchaToken?: string) => void
  reset: (
    email: string,
    confirmationCode: string,
    newPassword: string,
    recaptchaToken?: string
  ) => void
  signUp: ({
    email,
    password,
    signUpToken,
    recaptchaToken,
    referrerCode,
    marketingOptedInAt
  }: SignUpPayload) => void
  confirmEmail: (
    email: string,
    confirmationToken: string,
    recaptchaToken?: string
  ) => void
  generateOtp: () => void
  maybeRequestOtp: () => void
  logout: () => void
  refreshCurrentUser: () => Promise<UserType>
  setToast: (toast: IToast) => void
  verifyOtp: (params: VerifyOtpParams) => void
  setLoading: (loading: boolean) => void
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export function AuthProvider({
  children
}: Readonly<{
  children: JSX.Element
}>): JSX.Element {
  const [user, setUser] = useState<UserType | undefined>()
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true)
  const [mfaChallenge, setMfaChallenge] = useState<
    MfaChallengeState | undefined
  >()

  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false)

  const navigate = useNavigate()
  const location = useLocation()

  const [toast, setToast] = useState<IToast>({
    open: false,
    message: '',
    type: 'info',
    duration: 1500
  })

  // If we change page, reset the error state.
  useEffect(() => {
    if (error) setError(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // App-mount bootstrap: check stored tokens, refresh if needed, load user
  useEffect(() => {
    async function bootstrap() {
      const tokens = getTokens()

      if (tokens === null) {
        setUser(undefined)
        setLoadingInitial(false)
        return
      }

      try {
        if (isAccessTokenExpired()) {
          await refreshAccessToken()
        }

        await fetchCurrentUser()

        const claims = decodeAccessToken()
        if (claims?.legacy_mfa_pending === 'true') {
          navigate('/account/settings/two_step_authentication/re-enrol')
        }
      } catch {
        setUser(undefined)
      } finally {
        setLoadingInitial(false)
      }

      startMultiTabSync()
    }

    void bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function showToastError(error: any) {
    // Serverless rejects with an ApiError ({ message, code }) — no
    // response.data.errors map. Read defensively so the Rails-era shape, the
    // ApiError message, and a bare Error are all handled.
    const railsErrors = error?.response?.data?.errors
    const message: string =
      (railsErrors &&
        sentenceCase(
          `${Object.entries(railsErrors)
            .map(([, val]) => `${val}`)
            .join(', ')}.`
        )) ||
      error?.message ||
      'Something went wrong. Please try again.'
    setToast(prev => ({
      ...prev,
      open: true,
      message,
      type: 'error',
      duration: 3000
    }))
  }

  async function fetchCurrentUser(): Promise<UserType> {
    const userData = await usersApi.getCurrentUser()
    const userAsUserType = userData as unknown as UserType
    setUser(userAsUserType)
    Bugsnag.setUser(
      String(userAsUserType.id),
      userAsUserType.email ?? undefined
    )
    return userAsUserType
  }

  async function login({
    email,
    password,
    keepMeSignedIn,
    recaptchaToken
  }: LogInPayload) {
    if (loading) return undefined
    setLoading(true)
    setError('')

    const currentSearch = location.search

    sessionsApi
      .login({
        email,
        password,
        remember_me: !!keepMeSignedIn,
        recaptcha_token: recaptchaToken
      })
      .then(async res => {
        gaEvent('login', {
          name: 'a user logs in'
        })

        if (
          res.status === AuthorizationStatus.MfaChallenge &&
          res.session &&
          res.challengeName
        ) {
          setMfaChallenge({
            email,
            session: res.session,
            challengeName: res.challengeName
          })
          navigate(verifyOtpPath())
          return null
        }

        if (res.status === AuthorizationStatus.VerifyOtp) {
          navigate(verifyOtpPath())
          return null
        }

        if (res.accessToken && res.refreshToken && res.expiresAt) {
          setTokens({
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
            expiresAt: res.expiresAt
          })
          scheduleRefresh()
        }

        return await fetchCurrentUser()
      })
      .then(fetchedUser => {
        if (fetchedUser) {
          const userId = fetchedUser.id
          const userTheme = getUserTheme(String(userId))
          saveTheme(userTheme)

          const queryParams = queryString.parse(currentSearch)
          const path = queryParams.path
          navigate((path as string) || dashboardPath())
        }
      })
      .catch(error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errData = (error as any)?.response?.data
        // Serverless login failures (wrong password, unconfirmed user) now
        // reject as an ApiError ({ message, code }) via the response
        // interceptor — fall back to error.message when there's no Rails-style
        // response.data.
        const errMessage: string =
          errData?.errors?.[0] ||
          errData?.error ||
          errData?.message ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message ||
          ''
        const isUnconfirmed = errMessage.toLowerCase().includes('not confirmed')
        if (isUnconfirmed) {
          navigate(
            confirmEmailPath() + '?' + queryString.stringify({ email }),
            { state: { resendCode: true } }
          )
        }
        setError(
          errData?.errors
            ? sentenceCase(`${errData.errors.join(', ')}.`)
            : errData?.error ||
                errData?.message ||
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (error as any)?.message
        )
      })
      .finally(() => setLoading(false))
  }

  function signUp({
    email,
    password,
    signUpToken,
    recaptchaToken,
    referrerCode,
    marketingOptedInAt
  }: SignUpPayload) {
    if (loading) return
    setLoading(true)
    setError('')

    usersApi
      .signUp({
        email,
        password,
        signup_token: signUpToken,
        recaptcha_token: recaptchaToken,
        referrer_code: referrerCode,
        marketing_opt_in: marketingOptedInAt,
        // The ref / ch / utm tags of the visit that brought them, if any
        // (utils/attribution.ts); forgotten once the sign-up has used them.
        attribution: signupAttribution()
      })
      .then(() => {
        clearAttribution()
        gaEvent('sign_up', {
          name: 'a user signs up to measure the popularity of each sign-up method'
        })

        fbEvent('CompleteRegistration')

        saveTheme('device')
        // A six-digit confirmation code was just emailed [BUG-010, Cam 14 Aug:
        // "Sign Up > Check your Email with Code > Enter Code"] — land on the
        // code-entry screen with the address prefilled. The emailed link still
        // confirms directly for anyone who prefers to click it.
        navigate(confirmEmailPath() + '?' + queryString.stringify({ email }), {
          state: { registered: true }
        })
      })
      .catch(error => {
        // The serverless API returns a { status, message } envelope (or a thrown
        // Error with friendlyMessage), not the Rails { errors } map — extract
        // defensively so a missing field can't crash the handler. A
        // pre-formatted friendly message is shown as-is; only the raw Rails
        // errors-map is normalised through sentenceCase.
        const data = error?.response?.data
        const friendly =
          error?.friendlyMessage || data?.message || error?.message
        if (friendly) {
          setError(`${friendly}`)
        } else if (data?.errors) {
          setError(
            sentenceCase(
              `${Object.entries(data.errors)
                .map(([key, val]) => `${key} ${val}`)
                .join(', ')}.`
            )
          )
        } else {
          setError('Sign up failed. Please try again.')
        }
      })
      .finally(() => setLoading(false))
  }

  function confirmEmail(
    email: string,
    confirmationToken: string,
    recaptchaToken?: string
  ) {
    setLoading(true)
    setError('')

    usersApi
      .confirmEmail({
        email,
        confirmation_token: confirmationToken,
        recaptcha_token: recaptchaToken
      })
      .then(() => {
        setToast({
          open: true,
          message: 'Email confirmed. Please log in.',
          type: 'success',
          duration: 3000
        })
        navigate(loginPath())
      })
      .catch(error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err = error as any
        const errors = err?.response?.data?.errors
        const message = err?.response?.data?.message || err?.message
        setError(
          checkTypeOfValue(error) === 'string'
            ? error
            : errors
            ? sentenceCase(
                `${Object.entries(errors)
                  .map(([, val]) => `${val}`)
                  .join(', ')}.`
              )
            : message || 'An error occurred'
        )
      })
      .finally(() => setLoading(false))
  }

  async function resend(email: string, recaptchaToken?: string) {
    setLoading(true)

    usersApi
      .resendConfirmationEmail({ email, recaptcha_token: recaptchaToken })
      .then(() => {
        navigate(confirmEmailPath() + '?' + queryString.stringify({ email }))
      })
      .catch(error => {
        showToastError(error)
      })
      .finally(() => setLoading(false))
  }

  function requestReset(email: string, recaptchaToken?: string) {
    setLoading(true)

    usersApi
      .requestResetPassword({ email, recaptcha_token: recaptchaToken })
      .then(() => {
        // The serverless flow emails a 6-digit code; send the user to the reset
        // page (email pre-filled) to enter it, not back to login.
        navigate(resetPasswordPath() + '?' + queryString.stringify({ email }))
        setToast({
          open: true,
          message: 'Please check your email for a password reset code',
          type: 'info',
          duration: 3000
        })
      })
      .catch(error => {
        showToastError(error)
      })
      .finally(() => setLoading(false))
  }

  function reset(
    email: string,
    confirmationCode: string,
    newPassword: string,
    recaptchaToken?: string
  ) {
    setLoading(true)
    usersApi
      .resetPassword({
        email,
        confirmationCode,
        newPassword,
        recaptcha_token: recaptchaToken
      })
      .then(() => {
        navigate(loginPath())
        setToast({
          open: true,
          message: 'Password reset. Please log in with your new password.',
          type: 'success',
          duration: 3000
        })
      })
      .catch(error => {
        showToastError(error)
      })
      .finally(() => setLoading(false))
  }

  async function logout() {
    // Fire-and-forget — ignore errors
    sessionsApi.logout().catch(() => undefined)

    cancelRefresh()
    clearTokens()
    setUser(undefined)
    saveTheme('device')
    navigate(loginPath())
  }

  async function verifyOtp(params: VerifyOtpParams) {
    if (loading) return undefined
    setLoading(true)
    setError('')

    const currentSearch = location.search

    const resolvedParams: VerifyOtpParams = {
      email: params.email || mfaChallenge?.email || '',
      mfaCode: params.mfaCode,
      session: params.session || mfaChallenge?.session || '',
      challengeName: params.challengeName || mfaChallenge?.challengeName || '',
      recaptcha_token: params.recaptcha_token
    }

    sessionsApi
      .verifyOtp(resolvedParams)
      .then(async res => {
        if (res.accessToken && res.refreshToken && res.expiresAt) {
          setTokens({
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
            expiresAt: res.expiresAt
          })
          scheduleRefresh()
        }

        return await fetchCurrentUser()
      })
      .then(fetchedUser => {
        if (fetchedUser) {
          const userId = fetchedUser.id
          const userTheme = getUserTheme(String(userId))
          saveTheme(userTheme)
        }

        const queryParams = queryString.parse(currentSearch)
        const path = queryParams.path
        navigate(path as string)
      })
      .catch(error => {
        setError(error.response?.data?.errors ?? '')
      })
      .finally(() => setLoading(false))
  }

  function handleCloseAuthModal(_event?: never, reason?: string): void {
    if (reason !== 'escapeKeyDown' && reason !== 'backdropClick') {
      setIsAuthModalOpen(false)
      void fetchCurrentUser().catch(() => undefined)
    }
  }

  // The withdrawal step-up for customers WITHOUT an authenticator app: the
  // backend emails a 6-digit code (valid 5 minutes), and the AuthModal collects
  // it. P1 2026-08-04: this was a no-op stub ("Cognito handles MFA natively" —
  // true for LOGIN, not for the withdraw step-up), so for ~99% of customers
  // clicking Withdraw did nothing at all: no code, no modal, no error.
  function generateOtp(): void {
    apiClient
      .post('auth/otp/generate')
      .then(() => {
        setIsAuthModalOpen(true)
      })
      .catch((err: unknown) => {
        setToast({
          open: true,
          message:
            err instanceof Error
              ? err.message
              : 'Could not send your verification code',
          type: 'error',
          duration: 5000
        })
      })
  }

  function maybeRequestOtp(): void {
    if (!user?.authenticatorAppEnabled) {
      generateOtp()
    } else {
      setIsAuthModalOpen(true)
    }
  }

  async function refreshCurrentUser(): Promise<UserType> {
    setLoading(true)
    try {
      return await fetchCurrentUser()
    } finally {
      setLoading(false)
    }
  }

  const memoisedValue = useMemo(
    () => ({
      user,
      loading,
      error,
      setLoading,
      login,
      resend,
      requestReset,
      reset,
      signUp,
      confirmEmail,
      generateOtp,
      maybeRequestOtp,
      logout,
      refreshCurrentUser,
      setToast,
      verifyOtp
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, error]
  )

  return (
    <AuthContext.Provider value={memoisedValue}>
      <LoadingWrapper loading={loadingInitial}>{children}</LoadingWrapper>
      {user && user.profile?.onboarded && <PopUp />}
      <AuthModal isOpen={isAuthModalOpen} onClose={handleCloseAuthModal} />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toast.open}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        autoHideDuration={toast.duration}
        key={'top' + 'right'}
      >
        <Alert severity={toast.type} sx={{ width: '100%' }} variant='filled'>
          {toast.message}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => useContext(AuthContext)
